/**
 * WaveSystem - Manages wave/pack operations with EventBus integration
 *
 * Wraps WaveStateManager methods and emits events for cross-system communication.
 * Part of SYS-03 requirement from Phase 5 system extraction.
 *
 * This follows the Strangler Fig pattern established in Phase 3:
 * - WaveSystem wraps WaveStateManager methods (doesn't replace them)
 * - Adds EventBus integration for decoupled communication
 * - Enables testability through dependency injection
 *
 * Usage:
 *   import { WaveSystem, getWaveSystem } from './systems/WaveSystem.js';
 *
 *   // Create instance with dependency injection
 *   const waveSystem = new WaveSystem(gameState);
 *
 *   // Or use singleton pattern
 *   const waveSystem = getWaveSystem(gameState);
 *
 *   // Open pack (emits PACK_OPENED event on first-time success)
 *   waveSystem.openPack(milestone, items);
 */

import { eventBus, GameEvents } from '../utils/EventBus.js';

export class WaveSystem {
    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get the WaveStateManager instance from gameState
     * @returns {WaveStateManager} The wave state manager
     */
    get waveStateManager() {
        return this.gameState.waveStateManager;
    }

    /**
     * Get all available packs sorted by milestone
     * @returns {Array} Sorted array of pack objects
     */
    getAvailablePacks() {
        return this.waveStateManager.getAvailablePacks();
    }

    /**
     * Open a pack - charges miles on FIRST open, stores generated items, awards wavedust
     * Emits GameEvents.PACK_OPENED on first-time successful open (not on cached re-opens)
     * @param {number} milestone - The milestone identifier
     * @param {Array} generatedItems - Items generated by PackGenerator (only used on first open)
     * @returns {object|false} The pack data with items and wavedust earned, or false if failed
     */
    openPack(milestone, generatedItems = null) {
        // Check if pack was already opened (for event emission decision)
        const packBefore = this.waveStateManager.getAvailablePacks().find(p => p.milestone === milestone);
        const wasAlreadyOpened = packBefore?.opened || false;

        // Delegate to WaveStateManager
        const result = this.waveStateManager.openPack(milestone, generatedItems);

        // Emit event only on successful FIRST-TIME open (not on cached re-opens)
        if (result && !wasAlreadyOpened) {
            eventBus.emit(GameEvents.PACK_OPENED, {
                milestone,
                packQuality: result.packQuality,
                wavedustEarned: result.wavedustEarned,
                itemCount: result.storedItems?.length || 0
            });
        }

        return result;
    }

    /**
     * Confirm pack purchase after user has selected an item
     * Emits GameEvents.PACK_PURCHASED on success
     * @param {number} milestone - The milestone identifier
     * @returns {boolean} True if successful
     */
    confirmPackPurchase(milestone) {
        // Delegate to WaveStateManager
        const success = this.waveStateManager.confirmPackPurchase(milestone);

        // Emit event on success
        if (success) {
            eventBus.emit(GameEvents.PACK_PURCHASED, {
                milestone
            });
        }

        return success;
    }

    /**
     * Scrap a pack (decline/remove without selecting an item)
     * Emits GameEvents.WAVE_SCRAPPED on success
     * @param {number} milestone - The milestone identifier
     * @returns {boolean} True if successful
     */
    scrapPack(milestone) {
        // Use gameState.scrapWave if it exists, otherwise use waveStateManager.removeAvailablePack
        let success;
        if (typeof this.gameState.scrapWave === 'function') {
            success = this.gameState.scrapWave(milestone);
        } else {
            success = this.waveStateManager.removeAvailablePack(milestone);
        }

        // Emit event on success
        if (success) {
            eventBus.emit(GameEvents.WAVE_SCRAPPED, {
                milestone
            });
        }

        return success;
    }

    /**
     * Get wave tier name from quality value
     * @param {number} quality - Quality value (0.0 to 1.0)
     * @returns {string} Tier name
     */
    getWaveTier(quality) {
        return this.waveStateManager.getWaveTier(quality);
    }

    /**
     * Get count of unseen waves
     * @returns {number} Number of unseen available waves
     */
    getUnseenWaveCount() {
        return this.waveStateManager.getUnseenWaveCount();
    }

    /**
     * Mark all current waves as seen
     */
    markAllWavesSeen() {
        return this.waveStateManager.markAllWavesSeen();
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create WaveSystem singleton
 * @param {GameState} [gameState] - GameState reference (required on first call)
 * @returns {WaveSystem|null} WaveSystem instance, or null if not initialized
 */
export function getWaveSystem(gameState) {
    if (!_instance && gameState) {
        _instance = new WaveSystem(gameState);
    }
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetWaveSystem() {
    _instance = null;
}
